import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Modal, Radio, RadioGroup, Space, Spin, Table, Tag, Toast, Typography } from '@douyinfe/semi-ui';
import { IconDelete, IconPlus, IconTickCircle } from '@douyinfe/semi-icons';
import Turnstile from 'react-turnstile';
import { API, timestamp2string } from '../helpers';
import { renderQuota } from '../helpers/render';

const { Text, Title } = Typography;

function normalizeInput(value) {
    return value.trim();
}

function renderExpiryStatus(item) {
    if (!item || !item.token_id) {
        return '未知';
    }
    if (item.expire_duration_days > 0 && !item.first_used_time) {
        return `时限卡，尚未使用（首用后有效 ${item.expire_duration_days} 天）`;
    }
    if (!item.expires_at) {
        return '永不过期';
    }
    return timestamp2string(item.expires_at);
}

function renderDailyLimit(item) {
    if (!item || !item.token_id) {
        return '未知';
    }
    if (!item.daily_quota_limit || item.daily_quota_limit <= 0) {
        return '无限制';
    }
    return renderQuota(item.daily_quota_limit, 3);
}

function renderStatusTag(item) {
    if (!item) {
        return <Tag color="grey">未校验</Tag>;
    }
    if (item.eligible) {
        return <Tag color="green">可合并</Tag>;
    }
    return <Tag color="red">不可合并</Tag>;
}

function mapMergeReason(reason, item, referenceItem) {
    switch (reason) {
        case '与基准卡不属于同一账户':
            return '这张卡与对照卡不属于同一购买渠道，不能合并';
        case '与基准卡分组不一致':
            return '这张卡与对照卡不是同一卡类型，不能合并';
        case '与基准卡时效规则不一致': {
            const itemLimited = !!item?.expire_duration_days;
            const referenceLimited = !!referenceItem?.expire_duration_days;
            if (referenceItem) {
                if (itemLimited !== referenceLimited) {
                    return '不限时卡不能与限时卡合并';
                }
                if (itemLimited && referenceLimited && item?.expire_duration_days !== referenceItem?.expire_duration_days) {
                    return '这张限时卡与对照卡时长不同，不能合并';
                }
            }
            return '这张卡的时效规则与对照卡不同，不能合并';
        }
        case '当前令牌已激活，无法合并':
            return '这张限时卡已经开始使用，不能合并';
        case '与基准卡每日上限不一致':
            return '这张卡的每日上限与对照卡不同，不能合并';
        case '与基准卡模型限制不一致':
            return '这张卡的模型权限与对照卡不同，不能合并';
        case '与基准卡 IP 限制不一致':
            return '这张卡的访问限制与对照卡不同，不能合并';
        case '当前令牌为无限额度，无法合并':
            return '这张卡是无限额度卡，不能合并';
        case '当前令牌状态不可合并':
            return '这张卡当前状态不可合并';
        case '当前令牌已过期，无法合并':
            return '这张卡已过期，不能合并';
        case '当前令牌剩余额度为 0，无法合并':
            return '这张卡剩余额度为 0，不能合并';
        case '令牌不存在或已失效':
            return '这张卡不存在或已失效，不能合并';
        case '令牌格式不正确':
            return '这张卡的格式不正确，请检查后重新输入';
        case '重复输入':
            return '这张卡重复输入了，请删除重复项';
        case '令牌不能为空':
            return '请输入有效的卡密后再校验';
        default:
            return reason;
    }
}

const mergeRiskWarningItems = [
    '合并后，除目标卡外，其余参与合并的卡会被禁用并归零。',
    '被禁用的源卡不能继续调用，也不能再通过查卡页查询额度和调用记录。',
    '如需保留源卡历史，请在合并前先查询并导出记录。',
    '合并后请只使用目标卡继续查询和调用。',
];

function MergeRiskWarning() {
    return (
        <div
            style={{
                marginTop: 16,
                padding: '12px 16px',
                border: '1px solid #ffccc7',
                borderRadius: 8,
                background: '#fff1f0',
            }}
        >
            <Space vertical align="start" style={{ width: '100%' }}>
                <Text type="danger" strong>合并前请确认</Text>
                {mergeRiskWarningItems.map((item) => (
                    <Text key={item} type="danger">- {item}</Text>
                ))}
            </Space>
        </div>
    );
}

const TokenMergePanel = ({ baseUrl }) => {
    const [tokens, setTokens] = useState(['', '']);
    const [configLoading, setConfigLoading] = useState(false);
    const [configError, setConfigError] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [commitLoading, setCommitLoading] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileRenderKey, setTurnstileRenderKey] = useState(0);
    const [mergeConfig, setMergeConfig] = useState({
        turnstileEnabled: false,
        turnstileSiteKey: '',
        maxTokens: 10,
    });
    const [previewData, setPreviewData] = useState(null);
    const [selectedTarget, setSelectedTarget] = useState('');
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [commitResult, setCommitResult] = useState(null);

    useEffect(() => {
        const loadConfig = async () => {
            setConfigLoading(true);
            setConfigError('');
            try {
                const res = await API.get(`${baseUrl}/api/token/self/merge/config`);
                const payload = res?.data?.data;
                if (!res?.data?.success || !payload) {
                    throw new Error(res?.data?.message || '无法加载自助系统配置');
                }
                setMergeConfig({
                    turnstileEnabled: !!payload.turnstile_enabled,
                    turnstileSiteKey: payload.turnstile_site_key || '',
                    maxTokens: payload.max_tokens || 10,
                });
            } catch (error) {
                console.error(error);
                setConfigError(error?.response?.data?.message || error?.message || '无法加载自助系统配置');
            } finally {
                setConfigLoading(false);
            }
        };

        loadConfig();
        setPreviewData(null);
        setSelectedTarget('');
        setCommitResult(null);
        setTurnstileToken('');
        setTurnstileRenderKey((prev) => prev + 1);
    }, [baseUrl]);

    const nonEmptyTokens = useMemo(
        () => tokens.map(normalizeInput).filter((item) => item !== ''),
        [tokens],
    );

    const eligibleItems = previewData?.items?.filter((item) => item.eligible) || [];
    const selectedTargetItem = eligibleItems.find((item) => nonEmptyTokens[item.index] === selectedTarget);
    const selectedSourceItems = eligibleItems.filter((item) => nonEmptyTokens[item.index] !== selectedTarget);
    const referenceItem = previewData?.items?.find((item) => item.is_reference);
    const currentTransferQuota = selectedSourceItems.reduce((sum, item) => sum + (item?.total_available || 0), 0);

    const handleTokenChange = (index, value) => {
        const next = [...tokens];
        next[index] = value;
        setTokens(next);
    };

    const handleAddRow = () => {
        if (tokens.length >= mergeConfig.maxTokens) {
            Toast.warning(`单次最多输入 ${mergeConfig.maxTokens} 张令牌`);
            return;
        }
        setTokens([...tokens, '']);
    };

    const handleRemoveRow = (index) => {
        if (tokens.length <= 2) {
            Toast.warning('至少保留 2 个输入框');
            return;
        }
        setTokens(tokens.filter((_, currentIndex) => currentIndex !== index));
    };

    const handlePreview = async () => {
        if (nonEmptyTokens.length < 2) {
            Toast.warning('至少输入 2 张令牌后再校验');
            return;
        }
        if (!mergeConfig.turnstileEnabled) {
            Toast.warning('当前服务未配置 Turnstile，自助合并暂不可用');
            return;
        }
        if (mergeConfig.turnstileEnabled && !turnstileToken) {
            Toast.warning('请先完成验证后再校验令牌');
            return;
        }

        setPreviewLoading(true);
        setCommitResult(null);
        try {
            const res = await API.post(`${baseUrl}/api/token/self/merge/preview`, {
                tokens: nonEmptyTokens,
                turnstile_token: turnstileToken,
            });
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || '令牌校验失败');
            }
            const data = res.data.data;
            setPreviewData(data);
            setSelectedTarget(data.recommended_target_sk || '');
            if (!data.can_merge) {
                Toast.warning('存在不可合并令牌，请根据逐卡原因调整后重新校验');
            } else {
                Toast.success('令牌校验通过，请确认目标卡');
            }
        } catch (error) {
            console.error(error);
            const message = error?.response?.data?.message || error?.message || '令牌校验失败';
            Toast.error(message);
            setPreviewData(null);
            setSelectedTarget('');
        } finally {
            if (mergeConfig.turnstileEnabled) {
                setTurnstileToken('');
                setTurnstileRenderKey((prev) => prev + 1);
            }
            setPreviewLoading(false);
        }
    };

    const handleCommit = async () => {
        if (!previewData?.can_merge || !previewData?.preview_token || !selectedTarget) {
            return;
        }

        setCommitLoading(true);
        try {
            const res = await API.post(`${baseUrl}/api/token/self/merge/commit`, {
                target_sk: selectedTarget,
                preview_token: previewData.preview_token,
            });
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || '令牌合并失败');
            }
            setCommitResult(res.data.data);
            setConfirmVisible(false);
            setPreviewData(null);
            setSelectedTarget('');
            setTokens(['', '']);
            Toast.success(res.data.data?.message || '令牌合并成功');
        } catch (error) {
            console.error(error);
            const message = error?.response?.data?.message || error?.message || '令牌合并失败';
            Toast.error(message);
        } finally {
            setCommitLoading(false);
        }
    };

    const previewColumns = [
        {
            title: '序号',
            dataIndex: 'index',
            render: (_, item) => item.index + 1,
            width: 72,
        },
        {
            title: '令牌名称',
            dataIndex: 'token_name',
            render: (text) => text || '未知',
        },
        {
            title: '卡类型',
            dataIndex: 'group',
            render: (text) => text || '未知',
        },
        {
            title: '剩余额度',
            dataIndex: 'total_available',
            render: (value) => renderQuota(value || 0, 3),
        },
        {
            title: '有效期状态',
            dataIndex: 'expires_at',
            render: (_, item) => renderExpiryStatus(item),
        },
        {
            title: '每日上限',
            dataIndex: 'daily_quota_limit',
            render: (_, item) => renderDailyLimit(item),
        },
        {
            title: '校验状态',
            dataIndex: 'eligible',
            render: (_, item) => (
                <Space>
                    {item.is_reference ? <Tag color="blue">对照卡</Tag> : null}
                    {renderStatusTag(item)}
                </Space>
            ),
        },
        {
            title: '原因',
            dataIndex: 'reasons',
            render: (reasons, item) => reasons?.length > 0
                ? reasons.map((reason) => mapMergeReason(reason, item, referenceItem)).join('；')
                : '符合条件',
        },
    ];

    return (
        <>
            <Card style={{ marginTop: 24 }}>
                <Title heading={5}>令牌合并</Title>
                <Text type="secondary">
                    只有同一购买渠道、同一卡类型、同规则的卡才能合并。不限时卡只能和不限时卡合并。限时卡只有在还没开始使用时，才可以和同规则的限时卡合并。正价卡无法与折扣卡（如八折卡、五折卡）合并，活动卡也不能跨类型合并。每日上限、模型权限、访问限制不同的卡不能合并。合并后只保留目标卡规则，其余参与合并的卡将被禁用。
                </Text>
                <MergeRiskWarning />
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {tokens.map((token, index) => (
                        <Space key={`merge-token-${index}`} align="start" style={{ width: '100%' }}>
                            <Input
                                value={token}
                                onChange={(value) => handleTokenChange(index, value)}
                                placeholder={`请输入第 ${index + 1} 张令牌 sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}
                                style={{ flex: 1 }}
                            />
                            <Button
                                icon={<IconDelete />}
                                theme="borderless"
                                type="danger"
                                onClick={() => handleRemoveRow(index)}
                            >
                                删除
                            </Button>
                        </Space>
                    ))}
                    <Space>
                        <Button icon={<IconPlus />} theme="light" onClick={handleAddRow}>
                            添加令牌
                        </Button>
                        <Button
                            type="primary"
                            theme="solid"
                            loading={previewLoading}
                            onClick={handlePreview}
                            disabled={configLoading || !!configError || !mergeConfig.turnstileEnabled}
                        >
                            校验令牌
                        </Button>
                    </Space>
                    {configLoading ? <Spin /> : null}
                    {configError ? <Text type="danger">{configError}</Text> : null}
                    {!configError && mergeConfig.turnstileEnabled && mergeConfig.turnstileSiteKey ? (
                        <Turnstile
                            key={turnstileRenderKey}
                            sitekey={mergeConfig.turnstileSiteKey}
                            onVerify={(token) => setTurnstileToken(token)}
                        />
                    ) : null}
                    {!configError && !mergeConfig.turnstileEnabled ? (
                        <Text type="danger">当前服务未配置 Turnstile，自助合并暂不可用</Text>
                    ) : null}
                </div>
            </Card>

            {previewData ? (
                <Card style={{ marginTop: 24 }}>
                    <Space vertical style={{ width: '100%' }}>
                        <Title heading={6}>校验结果</Title>
                        <Text type={previewData.can_merge ? 'success' : 'danger'}>
                            {previewData.can_merge ? '这些卡符合合并条件，可以继续选择目标卡并确认合并。' : '存在不可合并的卡，请根据下方原因调整后重新校验。'}
                        </Text>
                        <Table
                            columns={previewColumns}
                            dataSource={previewData.items || []}
                            pagination={false}
                            rowKey={(record) => `${record.index}-${record.token_id || 'invalid'}`}
                        />
                        {previewData.can_merge ? (
                            <>
                                <Title heading={6}>选择目标卡</Title>
                                <RadioGroup value={selectedTarget} onChange={(event) => setSelectedTarget(event.target.value)}>
                                    <Space vertical align="start">
                                        {eligibleItems.map((item) => (
                                            <Radio key={`${item.token_id}-${item.token_name}`} value={nonEmptyTokens[item.index]}>
                                                {item.token_name}（剩余额度 {renderQuota(item.total_available, 3)}）
                                            </Radio>
                                        ))}
                                    </Space>
                                </RadioGroup>
                                <Card shaded>
                                    <Space vertical align="start">
                                        <Text>对照卡：{previewData.reference_token || '无'}</Text>
                                        <Text>当前目标卡：{selectedTargetItem?.token_name || '未选择'}</Text>
                                        <Text>系统建议：{previewData.recommended_target_sk || '无'}</Text>
                                        <Text>本次将转入总额度：{renderQuota(currentTransferQuota, 3)}</Text>
                                        <Text>确认后，除目标卡外，其余参与合并的卡将被禁用。</Text>
                                    </Space>
                                </Card>
                                <Button
                                    type="primary"
                                    theme="solid"
                                    icon={<IconTickCircle />}
                                    disabled={!selectedTarget}
                                    onClick={() => setConfirmVisible(true)}
                                >
                                    确认合并
                                </Button>
                            </>
                        ) : null}
                    </Space>
                </Card>
            ) : null}

            {commitResult ? (
                <Card style={{ marginTop: 24 }}>
                    <Space vertical align="start">
                        <Title heading={6}>合并结果</Title>
                        <Text>{commitResult.message}</Text>
                        <Text>目标卡：{commitResult.target?.token_name || '未知'}</Text>
                        <Text>转入额度：{renderQuota(commitResult.transferred_quota || 0, 3)}</Text>
                        <Text>已禁用源卡：{(commitResult.disabled_sources || []).join('、') || '无'}</Text>
                        <Text type="danger">后续请使用目标卡查询；源卡已不可查询。</Text>
                    </Space>
                </Card>
            ) : null}

            <Modal
                title="确认合并令牌"
                visible={confirmVisible}
                onCancel={() => setConfirmVisible(false)}
                onOk={handleCommit}
                confirmLoading={commitLoading}
                okText="确认合并"
                cancelText="取消"
            >
                <Space vertical align="start" style={{ width: '100%' }}>
                    <Text>目标卡：{selectedTargetItem?.token_name || selectedTarget || '未选择'}</Text>
                    <Text>源卡：{selectedSourceItems.map((item) => item.token_name).join('、') || '无'}</Text>
                    <Text>本次将转入总额度：{renderQuota(currentTransferQuota, 3)}</Text>
                    <Text>确认后，除目标卡外，其余参与令牌将被禁用且剩余额度归零，此操作不可逆。</Text>
                    <MergeRiskWarning />
                </Space>
            </Modal>
        </>
    );
};

export default TokenMergePanel;
